/**
 * Calendario de una campaña navideña: cuándo pasa el trineo por cada parada y
 * en qué fase está la experiencia.
 *
 * El modelo horario es el barrido real de la Nochebuena: el trineo pasa por
 * cada parada a las 22:00 **de su hora local**, así que su instante UTC sale de
 * restar el desfase. Ese mismo cálculo le da al visitante su franja aproximada
 * (`schedule.ts`), de modo que mapa y estimación nunca se contradicen.
 *
 * Funciones puras: el servidor calcula el estado para el HTML indexable y el
 * cliente lo recalcula cada segundo sin discrepancias de hidratación.
 */

import {
  DELIVERY_LOCAL_HOUR,
  DEPARTURE_LEAD_MINUTES,
  DEPARTURE_STOP,
  RETURN_FLIGHT_MINUTES,
  ROUTE,
} from './route'
import type { RouteStop, TrackerPhase, TrackerSeason } from './types'

export const MINUTE_MS = 60_000
export const HOUR_MS = 3_600_000
export const DAY_MS = 86_400_000

/** Horas antes del despegue en las que la página entra en «cuenta atrás final». */
export const LIFTOFF_WINDOW_HOURS = 12

/** Inicio de la franja local que se le anuncia al visitante. */
export const VISITOR_WINDOW_START_HOUR = 21

/** Fin de la franja local (medianoche). */
export const VISITOR_WINDOW_END_HOUR = 24

/**
 * Desfase con el que **el servidor** calcula la estimación: el de
 * `Europe/Madrid` en diciembre, que es la zona por defecto del sitio
 * (`i18n/request.ts`). El cliente lo sustituye por el real al montar; hasta
 * entonces el HTML tiene que ser el mismo en los dos lados.
 */
export const DEFAULT_UTC_OFFSET_HOURS = 1

/** Una hora concreta de un día de diciembre, en el calendario local. */
export interface DecemberMoment {
  year: number
  dayOfMonth: number
  localHour: number
}

/** Ese momento local, convertido a epoch ms UTC con el desfase indicado. */
export function localMomentToUtcMs(moment: DecemberMoment, utcOffsetHours: number): number {
  return (
    Date.UTC(moment.year, 11, moment.dayOfMonth, 0, 0, 0, 0) +
    (moment.localHour - utcOffsetHours) * HOUR_MS
  )
}

function deliveryMsFor(stop: RouteStop, year: number): number {
  return localMomentToUtcMs(
    { year, dayOfMonth: 24, localHour: DELIVERY_LOCAL_HOUR },
    stop.utcOffset
  )
}

/** Instante UTC en el que el trineo pasa por una parada de un año dado. */
export function stopArrivalMs(stop: RouteStop, year: number): number {
  if (stop.id === DEPARTURE_STOP.id) {
    return deliveryMsFor(ROUTE[1], year) - DEPARTURE_LEAD_MINUTES * MINUTE_MS
  }
  if (stop.leg === 'north-pole-return') {
    return deliveryMsFor(ROUTE[ROUTE.length - 2], year) + RETURN_FLIGHT_MINUTES * MINUTE_MS
  }
  return deliveryMsFor(stop, year)
}

/** Hitos de la campaña navideña de un año. */
export function seasonFor(year: number): TrackerSeason {
  const tripStartMs = deliveryMsFor(ROUTE[1], year) - DEPARTURE_LEAD_MINUTES * MINUTE_MS
  const lastDeliveryMs = deliveryMsFor(ROUTE[ROUTE.length - 2], year)
  return {
    year,
    liftoffStartMs: tripStartMs - LIFTOFF_WINDOW_HOURS * HOUR_MS,
    tripStartMs,
    lastDeliveryMs,
    tripEndMs: lastDeliveryMs + RETURN_FLIGHT_MINUTES * MINUTE_MS,
    // «Misión cumplida» se mantiene hasta fin de año; el 1 de enero la página
    // ya cuenta hacia la Navidad siguiente.
    completedUntilMs: Date.UTC(year, 11, 31, 23, 59, 59, 999),
  }
}

/** Todas las llegadas de una temporada, en el mismo orden que `ROUTE`. */
export function arrivalsFor(season: TrackerSeason): number[] {
  return ROUTE.map((stop) => stopArrivalMs(stop, season.year))
}

export function phaseAt(nowMs: number, season: TrackerSeason): TrackerPhase {
  if (nowMs < season.liftoffStartMs) return 'preparing'
  if (nowMs < season.tripStartMs) return 'liftoff'
  if (nowMs <= season.tripEndMs) return 'traveling'
  return 'completed'
}
